import React from 'react';
import { FaCheckSquare, FaTimes, FaLightbulb } from 'react-icons/fa';

const PersonalFinanceWisdom = () => {
  const wisdomSections = [
    {
      id: 'protection',
      title: 'Income & Debt Protection',
      icon: '🛡️',
      iconColor: 'text-blue-400',
      dos: [
        'Buy adequate Term Life Insurance (15-20x annual income).',
        'Secure a comprehensive Health Insurance policy.'
      ],
      donts: [
        "Don't mix insurance with investment.",
        "Don't hide medical history."
      ]
    },
    {
      id: 'emergency',
      title: 'Emergency Fund',
      icon: '🚑',
      iconColor: 'text-red-400',
      dos: [
        'Keep 6-12 months of living expenses in liquid assets.',
        'Replenish the fund immediately if used.'
      ],
      donts: [
        "Don't invest emergency money in volatile assets."
      ]
    },
    {
      id: 'investments',
      title: 'Investments & Goals',
      icon: '📊',
      iconColor: 'text-blue-400',
      dos: [
        'Link every investment to a specific financial goal.',
        'Start early to benefit from compounding.'
      ],
      donts: [
        "Don't stop SIPs during market corrections."
      ]
    },
    {
      id: 'retirement',
      title: 'Retirement Planning',
      icon: '👴',
      iconColor: 'text-orange-400',
      dos: [
        'Prioritize retirement planning over everything else.',
        'Pay off high-interest debts aggressively.'
      ],
      donts: [
        "Don't withdraw from retirement corpus for mid-term needs."
      ]
    },
    {
      id: 'education',
      title: "Children's Education",
      icon: '🎓',
      iconColor: 'text-purple-400',
      dos: [
        'Start education planning when child is young.',
        'Consider inflation in education costs (8-10% annually).',
        'Use equity-heavy portfolio for long-term goals.'
      ],
      donts: [
        "Don't underestimate future education costs.",
        "Don't use education funds for other purposes."
      ]
    },
    {
      id: 'marriage',
      title: "Children's Marriage",
      icon: '💒',
      iconColor: 'text-pink-400',
      dos: [
        'Plan wedding expenses 15-20 years in advance.',
        'Factor in cultural expectations and inflation.',
        'Keep marriage fund separate from other goals.'
      ],
      donts: [
        "Don't take loans for wedding expenses.",
        "Don't compromise retirement for marriage goals."
      ]
    }
  ];

  return (
    <div className="space-y-6" data-testid="personal-finance-wisdom">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border-b-4 border-[#34D399]">
        <div className="flex items-center justify-center space-x-3">
          <FaLightbulb className="text-[#FFD700]" size={28} />
          <h2 className="text-2xl font-bold text-[#FFD700]">Personal Finance Wisdom</h2>
        </div>
        <p className="text-gray-400 text-center mt-2">Essential Do's & Don'ts for a Secure Financial Future</p>
      </div>

      {/* Wisdom Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {wisdomSections.map((section) => (
          <div 
            key={section.id} 
            className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] hover:border-[#FFD700] transition-all"
            data-testid={`wisdom-${section.id}`}
          >
            {/* Section Title */}
            <div className="flex items-center space-x-3 mb-4">
              <span className="text-2xl">{section.icon}</span>
              <h3 className={`text-xl font-bold ${section.iconColor}`}>{section.title}</h3>
            </div>

            {/* DO's */}
            <div className="mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-5 h-5 bg-[#34D399] rounded flex items-center justify-center">
                  <FaCheckSquare className="text-white" size={12} />
                </div>
                <span className="text-[#34D399] font-bold">DO's</span>
              </div>
              <ul className="space-y-2 ml-7">
                {section.dos.map((item, idx) => (
                  <li key={idx} className="text-gray-300 flex items-start">
                    <span className="text-gray-500 mr-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* DON'Ts */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-5 h-5 bg-[#F87171] rounded flex items-center justify-center">
                  <FaTimes className="text-white" size={12} />
                </div>
                <span className="text-[#F87171] font-bold">DON'Ts</span>
              </div>
              <ul className="space-y-2 ml-7">
                {section.donts.map((item, idx) => (
                  <li key={idx} className="text-gray-300 flex items-start">
                    <span className="text-gray-500 mr-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Tips Section */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#D4AF37]">
        <h3 className="text-xl font-bold text-[#FFD700] mb-4 flex items-center space-x-2">
          <span>💡</span>
          <span>General Financial Tips</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300">
          <div>
            <p className="flex items-start space-x-2">
              <span className="text-[#34D399]">✓</span>
              <span>Always pay yourself first (save before spending)</span>
            </p>
            <p className="flex items-start space-x-2 mt-2">
              <span className="text-[#34D399]">✓</span>
              <span>Review your financial plan at least annually</span>
            </p>
            <p className="flex items-start space-x-2 mt-2">
              <span className="text-[#34D399]">✓</span>
              <span>Maintain a personal budget and track expenses</span>
            </p>
          </div>
          <div>
            <p className="flex items-start space-x-2">
              <span className="text-[#34D399]">✓</span>
              <span>Diversify investments across asset classes</span>
            </p>
            <p className="flex items-start space-x-2 mt-2">
              <span className="text-[#34D399]">✓</span>
              <span>Keep debt-to-income ratio below 40%</span>
            </p>
            <p className="flex items-start space-x-2 mt-2">
              <span className="text-[#34D399]">✓</span>
              <span>Create a will and nominate beneficiaries</span>
            </p>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-[#0A1929] rounded-lg p-4 text-gray-500 text-sm border border-[#2D5A8F]">
        <p className="font-medium mb-1 text-gray-400">Disclaimer:</p>
        <p>This information is for educational purposes only and should not be considered as personalized financial advice. Please consult with your financial advisor for specific recommendations tailored to your situation.</p>
      </div>
    </div>
  );
};

export default PersonalFinanceWisdom;
